import Spinner from './ui/spinner';
import { useState, useEffect } from 'react';

import {
  TrendingUp,
  Users,
  MessageSquare,
  Flag,
  MessageCircle,
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';
import { api } from '../services/client';
import { useTranslation } from '../i18n';
import { IDashboardStats, IInvitationCodeStats } from '../types';
import { Card, CardContent } from './ui/card';

const MONTH_MAP: Record<string, string> = {
  Jan: '1',
  Feb: '2',
  Mar: '3',
  Apr: '4',
  May: '5',
  Jun: '6',
  Jul: '7',
  Aug: '8',
  Sep: '9',
  Oct: '10',
  Nov: '11',
  Dec: '12',
};

const DAY_MAP: Record<string, string> = {
  Mon: 'dashboard.mon',
  Tue: 'dashboard.tue',
  Wed: 'dashboard.wed',
  Thu: 'dashboard.thu',
  Fri: 'dashboard.fri',
  Sat: 'dashboard.sat',
  Sun: 'dashboard.sun',
};

const translateMonth = (
  month: string,
  t: (key: string, params?: Record<string, string>) => string,
) => {
  const n = MONTH_MAP[month];

  if (!n) return month;

  const translated = t('dashboard.month', { n });
  // If translation is just the number (en locale), keep original English name
  return translated === n ? month : translated;
};

const translateDay = (day: string, t: (key: string) => string) => {
  const key = DAY_MAP[day];
  return key ? t(key) : day;
};

const B2bDashboard = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState<IDashboardStats | null>(null);
  const [, setCodeStats] = useState<IInvitationCodeStats[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [dashStats, invStats] = await Promise.all([
        api.get<IDashboardStats>('/backoffice/dashboard-stats'),
        api.get<IInvitationCodeStats[]>(
          '/backoffice/invitation-codes-stats/all',
        ),
      ]);
      setStats(dashStats);
      setCodeStats(invStats);
    } catch (err) {
      console.error('Failed to load stats:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Spinner />;
  }

  if (!stats) {
    return (
      <div className="p-4 lg:p-8 text-muted-foreground">
        Failed to load statistics
      </div>
    );
  }

  const statCards = [
    {
      label: t('dashboard.total_users'),
      value: stats.totalUsers,
      change: stats.userGrowthRate,
      positive: !stats.userGrowthRate.startsWith('-'),
      Icon: Users,
      iconBg: 'bg-blue-50',
      iconColor: 'text-blue-600',
      testId: 'Dashboard_General_totalUsersText',
    },
    {
      label: t('dashboard.active_rooms'),
      value: stats.totalRooms.toLocaleString(),
      change: stats.roomGrowthRate,
      positive: !stats.roomGrowthRate.startsWith('-'),
      Icon: MessageSquare,
      iconBg: 'bg-green-50',
      iconColor: 'text-green-600',
      testId: 'Dashboard_General_activeRoomsText',
    },
    {
      label: t('dashboard.pending_reports'),
      value: stats.pendingReports.toString(),
      change: stats.reportGrowthRate,
      positive: !stats.reportGrowthRate.startsWith('-'),
      Icon: Flag,
      iconBg: 'bg-orange-50',
      iconColor: 'text-orange-600',
      testId: 'Dashboard_General_pendingReportsText',
    },
    {
      label: t('dashboard.today_messages'),
      value:
        stats.todayMessages >= 1000000
          ? `${(stats.todayMessages / 1000000).toFixed(1)}M`
          : stats.todayMessages.toLocaleString(),
      change: stats.messageGrowthRate,
      positive: !stats.messageGrowthRate.startsWith('-'),
      Icon: MessageCircle,
      iconBg: 'bg-purple-50',
      iconColor: 'text-purple-600',
      testId: 'Dashboard_General_todayMessagesText',
    },
  ];

  return (
    <div className="p-4 lg:p-8 flex flex-col gap-8">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex flex-col gap-2">
          <h1 className="text-3xl font-bold text-gray-900">
            {t('dashboard.title')}
          </h1>
          <p className="text-base text-gray-600 mt-2">
            {t('dashboard.welcome')}
          </p>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map(
          ({
            label,
            value,
            change,
            positive,
            Icon,
            iconBg,
            iconColor,
            testId,
          }) => {
            const isPositive = positive ?? !change.startsWith('-');

            return (
              <Card key={label} className="shadow-sm border-0">
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-muted-foreground">
                        {label}
                      </p>
                      <p
                        className="text-3xl font-bold text-foreground mt-2"
                        data-testid={testId}
                      >
                        {value}
                      </p>
                      <div className="flex items-center gap-1 mt-2">
                        <TrendingUp
                          className={`w-4 h-4 ${isPositive ? 'text-green-600' : 'text-red-600'} ${!isPositive ? 'rotate-180' : ''}`}
                        />
                        <span
                          className={`text-sm font-medium ${isPositive ? 'text-green-600' : 'text-red-600'}`}
                        >
                          {change}
                        </span>
                        <span className="text-sm text-muted-foreground">
                          {t('common.vs_last_month')}
                        </span>
                      </div>
                    </div>
                    <div
                      className={`p-3 rounded-lg ${iconBg} ${iconColor} flex items-center justify-center shrink-0`}
                    >
                      <Icon className="w-6 h-6" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          },
        )}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Growth Trend */}
        <Card className="overflow-hidden">
          <div className="px-6 pt-6">
            <h3 className="text-base font-semibold text-foreground">
              {t('dashboard.user_growth')}
            </h3>
          </div>
          <div className="px-6 pb-6 pt-4">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart
                data={(stats.monthlyGrowth || []).map((item) => ({
                  ...item,
                  month: translateMonth(item.month, t),
                }))}
              >
                {' '}
                <CartesianGrid strokeDasharray="3 3" stroke="#ccc" />
                <XAxis dataKey="month" stroke="#666" />
                <YAxis stroke="#666" />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="total"
                  name={t('dashboard.user_count')}
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={{
                    r: 3,
                    fill: '#fff',
                    stroke: '#3b82f6',
                    strokeWidth: 2,
                  }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Weekly Activity */}
        <Card className="overflow-hidden">
          <div className="px-6 pt-6">
            <h3 className="text-base font-semibold text-foreground">
              {t('dashboard.weekly_activity')}
            </h3>
          </div>
          <div className="px-6 pb-6 pt-4">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={(stats.weeklyActivity || []).map((item) => ({
                  ...item,
                  day: translateDay(item.day, t),
                }))}
              >
                {' '}
                <CartesianGrid strokeDasharray="3 3" stroke="#ccc" />
                <XAxis dataKey="day" stroke="#666" />
                <YAxis stroke="#666" />
                <Tooltip />
                <Bar
                  dataKey="count"
                  name={t('dashboard.message_count')}
                  fill="#3B82F6"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Charts section end */}
    </div>
  );
};

export default B2bDashboard;
